<template>
    <DashboardSectionCard
        :title="t('vue.dashboard_overview_portfolio_title')"
        :subtitle="t('vue.dashboard_overview_portfolio_subtitle')"
        :link-href="route('domains.index')"
        :link-label="t('vue.dashboard_overview_portfolio_link')"
    >
        <div
            class="grid grid-cols-1 items-center gap-[21px] sm:grid-cols-[126px_1fr]"
        >
            <DashboardDonut :segments="segments">
                <span class="text-2xl font-semibold leading-none text-gray-900">
                    {{ domains.total }}
                </span>
                <span class="mt-1 text-[11px] leading-[14px] text-gray-500">
                    {{ t("vue.dashboard_overview_portfolio_unit") }}
                </span>
            </DashboardDonut>

            <div class="grid gap-2.5">
                <div
                    v-for="row in legendRows"
                    :key="row.label"
                    class="flex items-center justify-between gap-3 text-[13px] leading-[18px]"
                >
                    <span class="flex items-center gap-2 text-gray-600">
                        <i
                            class="h-2 w-2 rounded-full"
                            :style="{ backgroundColor: row.color }"
                        />
                        {{ row.label }}
                    </span>
                    <span class="font-medium text-gray-900">
                        {{ row.value }}
                    </span>
                </div>
            </div>
        </div>
    </DashboardSectionCard>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import route from "ziggy-js";
import { useTranslations } from "@/composables/useTranslations";
import DashboardSectionCard from "./DashboardSectionCard.vue";
import DashboardDonut, { type DonutSegment } from "./DashboardDonut.vue";
import type { IDomainPortfolio } from "@/types/Dashboard/IDashboardOverview";

interface LegendRow {
    label: string;
    value: number;
    color: string;
}

const props = defineProps<{
    domains: IDomainPortfolio;
}>();

const { t } = useTranslations();

const ACTIVE_COLOR = "#00c951";
const INACTIVE_COLOR = "#ff8f52";
const PARKED_COLOR = "#9fa6b2";
const SUBDOMAIN_COLOR = "#088bea";

const segments = computed<DonutSegment[]>(() => [
    { key: "active", value: props.domains.active, color: ACTIVE_COLOR },
    { key: "inactive", value: props.domains.inactive, color: INACTIVE_COLOR },
    { key: "parked", value: props.domains.parked, color: PARKED_COLOR },
]);

const legendRows = computed<LegendRow[]>(() => [
    {
        label: t("vue.dashboard_overview_portfolio_active"),
        value: props.domains.active,
        color: ACTIVE_COLOR,
    },
    {
        label: t("vue.dashboard_overview_portfolio_inactive"),
        value: props.domains.inactive,
        color: INACTIVE_COLOR,
    },
    {
        label: t("vue.dashboard_overview_portfolio_parked"),
        value: props.domains.parked,
        color: PARKED_COLOR,
    },
    {
        label: t("vue.dashboard_overview_portfolio_subdomains"),
        value: props.domains.subdomains,
        color: SUBDOMAIN_COLOR,
    },
]);
</script>
